<template>
  <div>
    <div v-if="formattedData.length === 0" class="p-4">
      <h6>Es sind noch keine Änderungen vorhanden.</h6>
    </div>
    <div v-for="(change, i) in formattedData" :key="change.key + ' ' + change.date + '-' + i" class="my-2">
      <div class="d-flex">
        <span v-if="change.isDokumentChange">
          {{ change.nameOfEditor }} hat das Dokument
          <b>{{ change.dokumentLabel }}</b>
          {{ change.dokumentAktion }}.
        </span>
        <span v-else>
          {{ change.nameOfEditor }} hat
          <b>{{ change.formattedKey }}</b>
          geändert.
        </span>
        <div
          style="color: rgba(0, 0, 0, 0.5); cursor: pointer"
          class="ml-6"
          :id="'teilnehmer-change-' + uniqueId + '-' + i"
        >
          {{ change.formattedDate }}
        </div>
        <b-tooltip :target="'teilnehmer-change-' + uniqueId + '-' + i" triggers="hover" placement="right">
          <span style="font-size: 1rem">{{ formatDate(change.date) }}</span>
        </b-tooltip>
      </div>

      <!-- Bei Dokumenten steht der Dateiname in oldValue/newValue und wird wie eine Wertänderung angezeigt -->
      <div class="d-flex p-2 align-items-center" style="gap: 12px">
        <span class="badge badge-secondary" style="font-size: 0.9rem">{{ change.oldValueFormatted }}</span>
        <span style="font-size: 20px">&rarr;</span>
        <span class="badge badge-secondary" style="font-size: 0.9rem">{{ change.newValueFormatted }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { format, formatDistanceToNow } from 'date-fns';
import de from 'date-fns/locale/de';
import {
  GESCHLECHT_OPTIONS,
  NATIONALITAET_OPTIONS,
  PASSART_OPTIONS,
  getDokumentTypLabelFromApiValue,
} from '@/core/vorgaenge/helpers/teilnehmer-felder';
import { germanCountryName } from '@/core/common/iso3166Countries';
import { parseFullUsername } from '@/core/common/helpers/utils.js';

const OPTIONS_BY_KEY = {
  Geschlecht: GESCHLECHT_OPTIONS,
  Nationalitaet: NATIONALITAET_OPTIONS,
  PassArt: PASSART_OPTIONS,
};

// Felder mit Länderbezug werden über den Länder-Store aufgelöst
const LAND_KEYS = ['Geburtsland', 'PassLand'];

// Beschriftungen je Feld-Key für den Änderungstext
const FORMAT_KEYS = {
  Geburtsdatum: 'das Geburtsdatum',
  Nationalitaet: 'die Nationalität',
  Geschlecht: 'das Geschlecht',
  Geburtsort: 'den Geburtsort',
  Geburtsland: 'das Geburtsland',
  PassNummer: 'die Passnummer',
  PassArt: 'die Passart',
  PassAusstellungsdatum: 'das Ausstellungsdatum',
  PassGueltigkeitsdatum: 'das Gültigkeitsdatum',
  PassOrt: 'den Ausstellungsort',
  PassLand: 'das Ausstellungsland',
};

export default {
  name: 'TeilnehmerChangeHistory',
  props: {
    data: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      uniqueId: this._uid,
    };
  },
  computed: {
    ...mapState({
      users: state => state.users['users'],
    }),
    formattedData() {
      if (!this.data) return [];
      return Object.entries(this.data)
        .reduce((acc, [changeDate, entry]) => {
          const { changes, lastModifiedUserId } = entry;
          for (const change of changes) {
            const { key, oldValue, newValue, additionalIdentifier } = change;
            // Dokument-Änderungen kommen mit dem Key "Datei", dem Dokumenttyp im
            // additionalIdentifier und dem Dateinamen in oldValue/newValue
            const isDokumentChange = key === 'Datei';
            if (!isDokumentChange && !FORMAT_KEYS[key]) continue;

            acc.push({
              key,
              date: changeDate,
              formattedDate: this.getDateDistanceToNow(changeDate),
              nameOfEditor: this.getNameOfEditor(lastModifiedUserId),
              isDokumentChange,
              dokumentLabel: isDokumentChange ? getDokumentTypLabelFromApiValue(additionalIdentifier) : null,
              dokumentAktion: newValue != null ? 'hochgeladen' : 'gelöscht',
              formattedKey: FORMAT_KEYS[key],
              oldValueFormatted: this.getValueFormatted(oldValue, key),
              newValueFormatted: this.getValueFormatted(newValue, key),
            });
          }
          return acc;
        }, [])
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },
  methods: {
    getNameOfEditor(lastModifiedUserId) {
      if (lastModifiedUserId == null) return 'SKR';
      if (lastModifiedUserId.startsWith('kunde-')) return 'Kund:in';
      if (lastModifiedUserId.startsWith('agentur-')) return 'Agentur';
      const editor = (this.users || []).find(user => user.id === lastModifiedUserId);
      return editor ? parseFullUsername(editor) : '-';
    },
    getDateDistanceToNow(dateString) {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: de });
    },
    formatDate(value) {
      return format(new Date(value), 'dd.MM.yyyy  HH:mm') + ' Uhr';
    },
    getValueFormatted(value, key) {
      if (value == null || value === '') return '---';
      if (key.toLowerCase().includes('datum')) {
        try {
          return format(new Date(value), 'dd.MM.yyyy');
        } catch {
          return String(value);
        }
      }
      if (LAND_KEYS.includes(key)) {
        return germanCountryName(value);
      }
      const options = OPTIONS_BY_KEY[key];
      if (options) {
        return options.find(option => option.value === value)?.text || String(value);
      }
      return String(value);
    },
  },
};
</script>
